import { type LoaderFunctionArgs, redirect } from 'react-router';

import type { QueryClient } from '@tanstack/react-query';

import type { SavedFilterSource } from '@enterprise/api';
import { ensureError, isNotDefined } from '@enterprise/core';

import { enterpriseApi } from '@/common/enterprise-api';
import { createSavedFiltersQuery } from '@/features/profile/saved-filters/utilities/create-saved-filters-query';
import { applicationStore } from '@/store/application';

/**
 * Creates a route loader that auto-applies the pinned default saved filter when the
 * user lands on a Package Manager or List Manager page with no `tags` query param.
 *
 * Doing this at the loader level — before any component renders — avoids the
 * mount-effect race where chips written via `useEffect` aren't observed by
 * sibling components that read the same nuqs key.
 *
 * @param source - Page scope (Package Manager / List Manager).
 */
export const defaultSavedFilterLoader = (source: SavedFilterSource) => {
  // Allows e.g. /datastream/packagemanager, /datastream/packagemanager/closed,
  // /datastream/listmanager/external, etc. — but not sub-pages like
  // /datastream/listmanager/:mailingListId/recipients.
  const validPathRegex = new RegExp(`/${source}(/external)?(/closed)?/?$`);

  return (queryClient: QueryClient) => {
    return async ({ request }: LoaderFunctionArgs) => {
      // Unauthenticated — let RouteGuard redirect to login.
      if (isNotDefined(applicationStore.getState().user)) {
        return null;
      }

      const url = new URL(request.url);

      // Already has chips — nothing to apply.
      if (url.searchParams.has('tags')) {
        return null;
      }

      // Skip nested pages that don't render the Search component.
      if (!validPathRegex.test(url.pathname)) {
        return null;
      }

      try {
        const result = await queryClient.ensureQueryData(createSavedFiltersQuery(enterpriseApi, source));

        if (result.failure || isNotDefined(result.data)) {
          return null;
        }

        const defaultSave = result.data.find((save) => {
          return save.isDefault;
        });

        if (isNotDefined(defaultSave)) {
          return null;
        }

        const tagsParam = defaultSave.chips
          .map((chip) => {
            return `${chip.key}:${chip.value}`;
          })
          .join('|');

        url.searchParams.set('tags', tagsParam);

        if (defaultSave.searchLogic === 'AND') {
          url.searchParams.set('logic', 'and');
        }

        return redirect(`${url.pathname}${url.search}`);
      } catch (error) {
        const e = ensureError(error);

        console.warn('Failed to apply default saved filter:', e.message);

        return null;
      }
    };
  };
};
